import React from "react";
import { useSelector } from "react-redux";
import {
  getComparator,
  getEntriesOptions,
  handleSearch,
  stableSort,
} from "./functions";
import TableData from "./TableData";
import TableHeading from "./TableHeading";
import TablePagination from "./TablePagination";
import TableTitle from "./TableTitle";

const styles = {
  paginated: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    margin: "0px 1vw",
    maxWidth: "20vw",
  },
  select: {
    margin: "0px 0.5vw",
  },
  searchGroup: {},
};

const SmartTable = ({
  title,
  headActions,
  headers,
  actions,
  tableData = [],
  setTableData,
  headerComponents,
  paginated,
  sortable,
  searchByLabel,
  searchByField,
  selectable,
  rowsPerPage: perPageRows,
  noPadding,
  limit,
  paginatedPage,
  handlePageChange,
}) => {
  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState("calories");
  const [rows, setRows] = React.useState([]);
  const [searchQuery, setSearchQuery] = React.useState();
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(perPageRows || 5);
  const online = useSelector((state) => state.networkStatus.isOnline);
  const isAppOffline = useSelector((state) => state.networkStatus.isAppOffline);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const safeTableData = Array.isArray(tableData) ? tableData : [];

  const handleSelectAll = (event) => {
    if (setTableData) {
      if (event) {
        setTableData(
          safeTableData.map((item) => {
            return { ...item, selected: true };
          })
        );
      } else {
        setTableData(
          safeTableData.map((item) => {
            return { ...item, selected: false };
          })
        );
      }
    }
  };

  const handleCheckChange = (value, index) => {
    let myIndex = index;

    if (page > 0) {
      myIndex = page * rowsPerPage + index;
    }

    let newData = [...safeTableData];

    if (newData[myIndex]) {
      newData[myIndex].selected = value;
    }

    if (limit !== undefined) {
      let count = 0;
      newData?.forEach((data) => (data?.selected === true ? count++ : count));
      if (count > limit) {
        if (newData[myIndex]) {
          newData[myIndex].selected = false;
        }
        alert(`You can't select more then ${limit}.`);
      }
    }
    if (setTableData) {
      setTableData([...newData]);
    }
  };

  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  React.useEffect(() => {
    const isSame = rows.length === safeTableData.length && rows.every((val, index) => val === safeTableData[index]);
    if (!isSame) {
      setRows(safeTableData);
    }
    if (!paginated) {
      setRowsPerPage(perPageRows || safeTableData.length);
    }
  }, [tableData]);

  React.useEffect(() => {
    if (searchByField && searchQuery) {
      setRows(handleSearch(safeTableData, searchByField, searchQuery));
    } else {
      setRows(safeTableData);
    }
  }, [searchQuery]);

  const handlePageChangeRef = React.useRef(handlePageChange);
  React.useEffect(() => {
    handlePageChangeRef.current = handlePageChange;
  }, [handlePageChange]);

  React.useEffect(() => {
    if (handlePageChangeRef.current) {
      handlePageChangeRef.current(
        stableSort(rows, getComparator(order, orderBy))?.slice(
          page * rowsPerPage,
          page * rowsPerPage + rowsPerPage
        )
      );
    }
  }, [rows, order, orderBy, page, rowsPerPage]);

  const EntriesComponent = () => {
    return (
      <label style={styles.paginated}>
        Show
        <select
          style={styles.select}
          name="datatable_length"
          aria-controls="datatable"
          className="custom-select custom-select-sm form-control form-control-sm"
          onChange={(e) => setRowsPerPage(parseInt(e.target.value))}
        >
          {getEntriesOptions(safeTableData, rowsPerPage)}
        </select>{" "}
        entries
      </label>
    );
  };

  // const emptyRows =
  // rowsPerPage - Math.min(rowsPerPage, rows.length - page * rowsPerPage);

  return (
    <div className="row">
      <div className="col-12">
        <div className={!noPadding && "card"}>
          <div className={!noPadding && "card-body"}>
            {title && (
              <TableTitle
                headerComponents={headerComponents}
                title={title}
                endActions={headActions}
              />
            )}

            {(paginated || searchByField) && (
              <div className="d-flex justify-content-between align-items-center mb-4">
                {paginated && !isAppOffline && online && <EntriesComponent />}
                {!isAppOffline && online && searchByField && (
                  <div
                    className="input-group search-input"
                    style={styles.searchGroup}
                  >
                    <span className="input-group-prepend">
                      <button type="button" className="btn btn-gradient-primary">
                        <i className="fas fa-search"></i>
                      </button>
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder={searchByLabel || "Search"}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                )}
              </div>
            )}
            {!isAppOffline && online ? (
              <div className={"table-responsive"}>
                <table
                  id="datatable"
                  className="table table-bordered dt-responsive nowrap"
                >
                  <TableHeading
                    data={headers || []}
                    hasActions={actions?.length > 0}
                    onRequestSort={handleRequestSort}
                    sortable={sortable}
                    selectable={selectable}
                    onSelectAll={(e) => handleSelectAll(e)}
                    order={order}
                  />

                  <TableData
                    actions={actions}
                    header={headers}
                    handleCheckChange={(value, index) => {
                      handleCheckChange(value, index);
                    }}
                    selectable={selectable}
                    data={stableSort(
                      rows,
                      getComparator(order, orderBy)
                    )?.slice(
                      page * rowsPerPage,
                      page * rowsPerPage + rowsPerPage
                    )}
                    rowsPerPage={rowsPerPage}
                    page={page}
                  />
                </table>
              </div>
            ) : (
              <div></div>
            )}
            {!isAppOffline &&
              online &&
              (paginated || paginatedPage) &&
              safeTableData.length > 0 && (
                <TablePagination
                  count={rows.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onChangePage={handleChangePage}
                  onChangeRowsPerPage={handleChangeRowsPerPage}
                />
              )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SmartTable;
